import { HTMLInputTypeAttribute, ReactNode } from 'react';
import { Controller, useFormContext, FieldPath, FieldValues, get } from 'react-hook-form';
import { useOpenFundFieldTracking } from '../OpenFundAnalyticsContext';
import styles from './FormField.module.scss';

interface BaseFieldProps<T extends FieldValues> {
  name: FieldPath<T>;
  label: string;
  optional?: boolean;
  width?: string;
  className?: string;
}

interface TextFieldProps<T extends FieldValues> extends BaseFieldProps<T> {
  type?: HTMLInputTypeAttribute;
  placeholder?: string;
  multiline?: boolean;
  rows?: number;
  /** Rewrites what the user typed before it reaches the form, e.g. to drop disallowed characters. */
  transform?: (value: string) => string;
}

export function TextField<T extends FieldValues>({
  name,
  label,
  type = 'text',
  placeholder,
  multiline,
  rows = 3,
  transform,
  optional,
  width,
  className,
}: TextFieldProps<T>) {
  const {
    control,
    formState: { errors },
  } = useFormContext<T>();
  const trackField = useOpenFundFieldTracking();
  const error = get(errors, name);
  const fieldName = String(name);

  return (
    <div className={`${styles.fieldWrapper} ${className || ''}`} style={width ? { width } : undefined}>
      <label className={`body-sm-regular ${styles.label}`}>
        {label}
        {optional && <span className={styles.optional}>({'\u00A0'}לא חובה{'\u00A0'})</span>}
      </label>
      <Controller
        control={control}
        name={name}
        render={({ field }) => {
          const commonProps = {
            ...field,
            value: (field.value as string | number | undefined) ?? '',
            className: `${styles.input} body-md-regular ${error ? styles.invalid : ''}`,
            placeholder,
            'aria-invalid': !!error,
            onFocus: () => trackField(fieldName),
            onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
              field.onChange(transform ? transform(event.target.value) : event);
              trackField(fieldName);
            },
          };
          if (multiline) {
            return <textarea {...commonProps} rows={rows} />;
          }
          return <input {...commonProps} type={type} />;
        }}
      />
      {error && (
        <span className={`body-sm-regular ${styles.errorMessage}`}>
          {error.message as ReactNode}
        </span>
      )}
    </div>
  );
}
